import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import HilltopBanner from '../components/HilltopBanner';

const ICONS = ['analytics', 'fitness_center', 'savings', 'calculate', 'bar_chart', 'eco'];

function CalculatorsListing() {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [calculatorsPerPage] = useState(6);

  useEffect(() => {
    const fetchCalculators = async () => {
      try {
        const response = await apiService.getCalculators();
        if (response.success) {
          setCalculators(response.data);
        }
      } catch (error) {
        console.error('Error fetching calculators:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCalculators();
  }, []);

  const indexOfLastCalculator = currentPage * calculatorsPerPage;
  const indexOfFirstCalculator = indexOfLastCalculator - calculatorsPerPage;
  const currentCalculators = calculators.slice(indexOfFirstCalculator, indexOfLastCalculator);
  const totalPages = Math.ceil(calculators.length / calculatorsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12">
      <SEO
        title="CalcBlog - Compute Engines Registry"
        description="Browse our full registry of high-performance interactive calculators for health, finance, ecology, and more."
        keywords="calcblog calculators, bmi calculator, calorie calculator, carbon footprint, budget calculator"
        toolType="calculator"
      />

      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-20 space-y-4">
          <span className="label-caps text-brand">Calculators</span>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]">
            ALL CALCULATORS
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl leading-relaxed">
            Explore our collection of helpful calculators for various needs.
          </p>
        </div>

        {/* Ad Section */}
        <div className="flex justify-center py-8 border-y border-white/5 mb-12">
          <HilltopBanner />
        </div>

        {/* Calculators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {currentCalculators.map((calculator, idx) => (
            <Link
              key={calculator.id}
              to={`/calculators/${calculator.slug}`}
              className="glass group flex flex-col items-start text-left p-8 hover:scale-[1.01] transition-all duration-300 focus-glow focus:outline-none rounded-3xl"
            >
              {/* Calculator Header Image */}
              <div className="relative w-full aspect-[16/8] rounded-2xl overflow-hidden border border-white/5 mb-6 bg-black/20">
                <img
                  src={['/images/calc3_gen.jpeg', '/images/calc4_gen.png', '/images/calc5_gen.png', '/images/calc6_gen.jpeg', '/images/calc7_gen.jpeg', '/images/calc8_gen.png'][idx % 6]}
                  alt={calculator.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500 opacity-80 group-hover:opacity-100"
                />
              </div>

              <div className="w-12 h-12 glass rounded-xl flex items-center justify-center mb-6 group-hover:bg-brand/20 transition-colors border border-brand/20 group-hover:border-brand/50">
                <span className="material-symbols-outlined text-brand group-hover:scale-110 transition-transform text-2xl">
                  {ICONS[idx % ICONS.length]}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-brand transition-colors leading-tight">
                {calculator.name}
              </h3>
              <p className="text-slate-400 line-clamp-2 flex-grow mb-8">
                {calculator.description}
              </p>
              <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 group-hover:text-brand transition-colors tracking-widest uppercase">
                Launch Calculator
                <span className="material-symbols-outlined text-xs group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination & footer */}
        <div className="border-t border-white/5 pt-12 space-y-12">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-12 h-12 flex items-center justify-center rounded-full glass transition-all ${currentPage === 1 ? 'opacity-20 cursor-not-allowed' : 'text-brand hover:bg-brand/10'
                  }`}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`min-w-[48px] h-12 flex items-center justify-center rounded-full font-bold text-xs tracking-widest transition-all focus:ring-2 focus:ring-brand/40 outline-none ${currentPage === number
                      ? 'bg-brand text-[#050805] shadow-[0_0_20px_rgba(0,255,136,0.5)]'
                      : 'glass text-slate-400 hover:text-brand'
                      }`}
                  >
                    {number.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-12 h-12 flex items-center justify-center rounded-full glass transition-all ${currentPage === totalPages ? 'opacity-20 cursor-not-allowed' : 'text-brand hover:bg-brand/10'
                  }`}
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          )}


        </div>

        {/* Empty State */}
        {calculators.length === 0 && (
          <div className="text-center py-32 glass rounded-2xl">
            <span className="material-symbols-outlined text-6xl text-slate-600 mb-6">memory</span>
            <h3 className="text-2xl font-bold text-brand mb-2">No Calculators found</h3>
            <p className="text-slate-400">No Calculators found.</p>
          </div>
        )}
      </div>
    </div >
  );
}

export default CalculatorsListing;